'use client';

import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { Mail, ArrowLeft } from 'lucide-react';
import { Input } from '@schoolerp/ui';
import Image from 'next/image';
import Head from 'next/head';
import { ForgotPasswordForm } from 'packages/frontend/src/types/auth';
import { forgotPasswordSchema } from 'packages/frontend/src/schemas/auth.schema';
import BackgroundImage from 'public/images/signup-bg.jpg';
import ContainerImage from 'public/images/signup-container.jpg';

import { useAuth } from '../../../hooks/useAuth';
import { useToast } from '../../../contexts/ToastContext';
import { Loader } from '../../../components/common/Loader';
import { APP_CONSTANTS } from '../../../constants/app.constants';
import { nestLogoWithName } from '../../../constants/brand/brandLogos';

export default function ForgotPasswordPage() {
  const { forgotPassword, isLoading, error } = useAuth();
  const [isSuccess, setIsSuccess] = useState(false);
  const { addToast } = useToast();

  // Loading state for images and content visibility
  const [containerLoaded, setContainerLoaded] = useState(false);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [backgroundImgError, setBackgroundImgError] = useState(false);
  const [containerImgError, setContainerImgError] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  const backgroundImgRef = useRef<HTMLImageElement | null>(null);
  const containerImgRef = useRef<HTMLImageElement | null>(null);

  // Track screen size to decide gating behavior (lg breakpoint: 1024px)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsLargeScreen(media.matches);
    update();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', update);
    } else {
      media.addListener(update);
    }
    return () => {
      if (typeof media.removeEventListener === 'function') {
        media.removeEventListener('change', update);
      } else {
        media.removeListener(update);
      }
    };
  }, []);

  // On large screens, wait for container image; on small, show immediately
  useEffect(() => {
    if (!isLargeScreen) {
      setContentVisible(true);
      return;
    }
    if (containerLoaded || containerImgError) {
      setContentVisible(true);
    }
  }, [isLargeScreen, containerLoaded, containerImgError]);

  // Handle background image load and errors
  const handleBackgroundLoad = () => {
    setBackgroundLoaded(true);
  };
  const handleBackgroundError = () => {
    setBackgroundImgError(true);
    addToast('Background image could not be loaded.', 'error');
  };
  // Handle container image load and errors
  const handleContainerLoad = () => {
    setContainerLoaded(true);
  };
  const handleContainerError = () => {
    setContainerImgError(true);
    addToast('Some images could not be loaded.', 'error');
  };

  useEffect(() => {
    if (!backgroundLoaded && !backgroundImgError && backgroundImgRef.current?.complete) {
      setBackgroundLoaded(true);
    }
  }, [backgroundLoaded, backgroundImgError]);

  useEffect(() => {
    if (!containerLoaded && !containerImgError && containerImgRef.current?.complete) {
      setContainerLoaded(true);
    }
  }, [containerLoaded, containerImgError]);

  // Removed settings/banner logic (feature disabled)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      await forgotPassword(data.email);
      setIsSuccess(true);
    } catch (err) {
      console.error('Error sending reset link');
      // Error is handled by the hook
    }
  };

  return (
    <>
      {/* Loader overlay - shown until content is ready */}
      {!contentVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <Loader size="lg" containerClassName="text-white" />
        </div>
      )}

      {/* Main content - always rendered but initially hidden */}
      <div
        className={`min-h-screen relative flex items-center justify-center p-4 ${!contentVisible ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`}
      >
        <div className="absolute inset-0">
          {!backgroundImgError ? (
            <img
              ref={backgroundImgRef}
              src={BackgroundImage.src}
              className="absolute inset-0 w-full h-full object-cover object-center"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              onLoad={handleBackgroundLoad}
              onError={handleBackgroundError}
              style={{ visibility: backgroundLoaded ? 'visible' : 'hidden' }}
            />
          ) : (
            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-500">
              <p>Background image failed to load.</p>
            </div>
          )}
          <div className="absolute inset-0 bg-[#0D373D]/50" />
        </div>
        <div className="w-full max-w-[1200px] h-[720px] bg-white rounded-2xl shadow-[0_16px_50px_-12px_rgba(0,0,0,0.15)] overflow-hidden flex relative z-10">
          {/* Left Panel - Form */}
          <div className="w-full lg:w-[60%] p-8 lg:p-12 flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <Link
                href="/login"
                className="flex items-center gap-2 text-[#0D373D] hover:text-[#0D373D]/80 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back to login</span>
              </Link>
            </div>
            <div className="flex-1 flex items-center">
              <div className="w-full max-w-[400px] mx-auto">
                {/* Logo */}
                <div className="flex items-center justify-start mb-4">
                  <Image
                    src={nestLogoWithName.src}
                    alt={`${APP_CONSTANTS.APP_NAME} Logo`}
                    width={96}
                    height={24}
                    className="h-6 w-auto -ml-1"
                  />
                </div>
                <h1 className="text-3xl font-bold text-[#0D373D] mb-3">
                  Reset Password
                </h1>
                <p className="text-[#4D4F52] mb-8">
                  Enter your email address and we'll send you instructions to
                  reset your password.
                </p>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-[#4D4F52] mb-2"
                    >
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-[#4D4F52]/50 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        id="email"
                        {...register('email')}
                        type="text"
                        placeholder="Enter your email address"
                        disabled={isLoading}
                        className="pl-10 w-full rounded-xl border-[#E4E8EA] focus:border-[#0D373D] focus:ring-[#0D373D]/10 placeholder-[#4D4F52]/50 py-2.5"
                        aria-describedby="email-error"
                      />
                    </div>
                    {errors.email?.message && (
                      <p id="email-error" className="text-sm text-red-600 mt-1">
                        {errors.email?.message}
                      </p>
                    )}
                  </div>

                  {isSuccess && (
                    <div className="rounded-xl bg-green-50 p-4 border border-green-100">
                      <p className="text-sm text-green-700">
                        If an account exists with this email, you will receive a
                        password reset link shortly.
                      </p>
                    </div>
                  )}

                  {error && (
                    <div className="rounded-xl bg-red-50 p-4 border border-red-100">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 px-4 bg-[#0D373D] text-white rounded-xl hover:bg-[#0D373D]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0D373D]/10 text-sm font-medium transition-colors duration-200"
                    >
                      {isLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[#E4E8EA]"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-[#4D4F52]">
                        Remember your password?
                      </span>
                    </div>
                  </div>

                  <Link
                    href="/login"
                    className="w-full flex justify-center py-3.5 px-4 border border-[#E4E8EA] rounded-xl text-sm font-medium text-[#4D4F52] bg-white hover:bg-[#F5F8F9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0D373D]/10 transition-colors duration-200"
                  >
                    Back to Login
                  </Link>
                </form>
              </div>
            </div>
          </div>

          {/* Right Panel - Image */}
          <div className="hidden lg:block w-[40%] relative overflow-hidden">
            {!containerImgError ? (
              <img
                ref={containerImgRef}
                src={ContainerImage.src}
                // alt="Happy Students"
                width={ContainerImage.width}
                height={ContainerImage.height}
                className="absolute inset-0 w-full h-full object-cover object-center !max-w-none"
                loading="eager"
                decoding="sync"
                onLoad={handleContainerLoad}
                onError={handleContainerError}
                fetchPriority="high"
              />
            ) : (
              <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-500">
                <p>Container image failed to load.</p>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D373D]/95 via-[#0D373D]/40 to-transparent" />
            <div className="absolute inset-0 flex items-end p-12">
              <div className="relative z-10 space-y-4 max-w-[320px]">
                <h2 className="text-5xl font-bold text-white leading-tight">
                  Password
                  <br />
                  Recovery
                </h2>
                <p className="text-xl text-white/90 font-light">
                  We'll help you reset your password and get back to managing
                  your institution
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Head>
        <link
          rel="preload"
          href="/images/signup-bg.jpg"
          as="image"
          fetchPriority="high"
        />
        <link
          rel="preload"
          href="/images/signup-container.jpg"
          as="image"
          fetchPriority="high"
        />
      </Head>
    </>
  );
}
